/**
 * Wykaz akt osobowych — części A–E (Etap 40, ADR-0041).
 *
 * § 3 rozporządzenia o dokumentacji pracowniczej dzieli akta na pięć części,
 * a § 4 każe trzymać dokumenty chronologicznie, numerować je i prowadzić wykaz
 * dla każdej części. Numer bierze się z porządku, nie z pola — więc po
 * usunięciu dokumentu (na przykład po zatarciu kary) wykaz przenumerowuje się
 * sam.
 *
 * Zakładka „Dokumenty" w aktach była do tej pory stanem pustym; teraz jest tym
 * wykazem. Dokumentacja płacowa i czasu pracy stoi osobno, bo art. 94 pkt 9a
 * k.p. każe przechowywać ją odrębnie od akt osobowych.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Card, EmptyState, InfoBanner, Select, Spinner, useToast } from '@k1p4/ui'
import { formatDate } from '@k1p4/i18n'
import { PERSONNEL_FILE_PARTS } from '@k1p4/shared'
import { api } from '../../api/client'
import styles from './FamilyMembers.module.css'

export interface PersonnelFileProps {
  employeeId: string
  /** Przenoszenie między częściami wymaga uprawnienia „Dokumenty : Edycja". */
  canEdit: boolean
}

export function PersonnelFile({ employeeId, canEdit }: PersonnelFileProps) {
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const index = useQuery({
    queryKey: ['personnel-file', employeeId],
    queryFn: () => api.personnelFile(employeeId),
  })

  const move = useMutation({
    mutationFn: (input: { documentId: string; part: string | null }) =>
      api.setDocumentFilePart(input.documentId, input.part),
    onSuccess: () => {
      toast.show(t('personnelFile.moved'), { tone: 'success' })
      setError(null)
      void queryClient.invalidateQueries({ queryKey: ['personnel-file', employeeId] })
    },
    onError: () => setError('personnelFile.errorGeneric'),
  })

  if (index.isPending) return <Spinner label={t('personnelFile.loading')} />
  if (index.isError || !index.data) {
    return <EmptyState title={t('common.error')} description={t('common.retry')} />
  }

  const data = index.data
  const empty =
    data.parts.every((part) => part.entries.length === 0 && part.subparts.length === 0) &&
    data.outside.length === 0

  function partSelect(documentId: string, current: string | null) {
    if (!canEdit) return null
    return (
      <Select
        aria-label={t('personnelFile.movePart')}
        value={current ?? ''}
        onChange={(event) =>
          move.mutate({
            documentId,
            part: event.target.value === '' ? null : event.target.value,
          })
        }
      >
        <option value="">{t('personnelFile.outsideOption')}</option>
        {PERSONNEL_FILE_PARTS.map((part) => (
          <option key={part} value={part}>
            {t('personnelFile.partOption', { part })}
          </option>
        ))}
      </Select>
    )
  }

  return (
    <section className={styles.section}>
      <div className={styles.head}>
        <h3 className={styles.title}>{t('personnelFile.title')}</h3>
      </div>
      <p className={styles.note}>{t('personnelFile.lead')}</p>

      {error !== null ? (
        <InfoBanner tone="danger" className={styles.banner}>
          {t(error)}
        </InfoBanner>
      ) : null}

      {empty ? (
        <EmptyState title={t('personnelFile.empty')} description={t('personnelFile.emptyBody')} />
      ) : (
        <>
          {data.parts.map((part) => (
            <div key={part.part} className={styles.list}>
              <h4 className={styles.title}>
                {t('personnelFile.partTitle', { part: part.part })}
              </h4>
              <p className={styles.note}>{t(`personnelFile.parts.${part.part}`)}</p>
              {part.entries.length === 0 && part.subparts.length === 0 ? (
                <p className={styles.note}>{t('personnelFile.partEmpty')}</p>
              ) : (
                <Card pad="flush" className={styles.list}>
                  {part.entries.map((entry) => (
                    <div key={entry.documentId} className={styles.row}>
                      <span className={styles.rowName}>
                        {entry.label} · {entry.title}
                      </span>
                      <span className={styles.rowMeta}>
                        {t(`hrDocuments.types.${entry.type}`, { defaultValue: entry.type })}
                        {entry.date ? ` · ${formatDate(entry.date, 'dot')}` : ''}
                      </span>
                      {partSelect(entry.documentId, part.part)}
                    </div>
                  ))}
                  {/*
                    Części D i E dzielą się na podczęści — po jednej na każdą
                    sprawę (§ 4). Po usunięciu jednej pozostałe dostają kolejne
                    numery same, bo numer wynika z porządku.
                  */}
                  {part.subparts.map((subpart) => (
                    <div key={subpart.label} className={styles.row}>
                      <span className={styles.rowName}>
                        {t('personnelFile.subpart', { label: subpart.label })}
                      </span>
                      <span className={styles.rowMeta}>
                        {subpart.entries
                          .map((entry) => `${entry.label} · ${entry.title}`)
                          .join(' · ')}
                      </span>
                    </div>
                  ))}
                </Card>
              )}
            </div>
          ))}

          {data.outside.length > 0 ? (
            <div className={styles.list}>
              <h4 className={styles.title}>{t('personnelFile.outsideTitle')}</h4>
              <p className={styles.note}>{t('personnelFile.outsideLead')}</p>
              <Card pad="flush" className={styles.list}>
                {data.outside.map((entry) => (
                  <div key={entry.documentId} className={styles.row}>
                    <span className={styles.rowName}>{entry.title}</span>
                    <span className={styles.rowMeta}>
                      {t(`hrDocuments.types.${entry.type}`, { defaultValue: entry.type })}
                      {entry.date ? ` · ${formatDate(entry.date, 'dot')}` : ''}
                    </span>
                    {partSelect(entry.documentId, null)}
                  </div>
                ))}
              </Card>
            </div>
          ) : null}
        </>
      )}
    </section>
  )
}
